import React, { useEffect, useState } from 'react';
import { useAuth, errorMessage } from '../../context/AuthContext';
import { formatRange, formatMonth, byRecent } from '../../utils/cvFormat';

const newId = () => Math.random().toString(36).slice(2, 10);

const EMPLOYMENT_TYPES = ['', 'CDI', 'CDD', 'Stage', 'Alternance', 'Freelance', 'Indépendant', 'Intérim', 'Temps partiel', 'Bénévolat'];
const WORK_MODES = ['', 'Sur site', 'Hybride', 'À distance'];

// Description des champs de chaque section (utilisée pour générer les formulaires)
const SECTIONS = {
  experiences: {
    label: 'Expériences', item: 'expérience', sortStart: 'start',
    empty: () => ({ id: newId(), title: '', company: '', employment_type: '', location: '', work_mode: '', start: '', end: '', current: false, description: '', skills: '', logo: '' }),
    summary: (e) => ({ title: e.title || 'Nouvelle expérience', sub: [e.company, formatRange(e.start, e.end, e.current, false)].filter(Boolean).join(' · ') }),
    fields: [
      { tr: true, name: 'title', label: 'Poste', required: true, placeholder: 'ex : Développeuse full-stack' },
      { name: 'company', label: 'Entreprise / organisation', required: true },
      { name: 'employment_type', label: 'Type de contrat', type: 'select', options: EMPLOYMENT_TYPES },
      { name: 'work_mode', label: 'Mode de travail', type: 'select', options: WORK_MODES },
      { tr: true, name: 'location', label: 'Lieu', placeholder: 'ex : Paris, France' },
      { name: 'logo', label: 'Logo (facultatif)', type: 'image' },
      { name: 'start', label: 'Début', type: 'month', required: true },
      { name: 'end', label: 'Fin', type: 'month', disabledIf: 'current' },
      { name: 'current', label: 'J\'occupe actuellement ce poste', type: 'checkbox', full: true },
      { tr: true, name: 'description', label: 'Description', type: 'textarea', full: true, placeholder: 'Missions, réalisations, résultats… (retours à la ligne conservés)' },
      { tr: true, name: 'skills', label: 'Compétences (séparées par des virgules)', full: true, placeholder: 'React, Node.js, AWS' },
    ],
  },
  education: {
    label: 'Formations', item: 'formation', sortStart: 'start',
    empty: () => ({ id: newId(), school: '', degree: '', field: '', start: '', end: '', grade: '', description: '', logo: '' }),
    summary: (e) => ({ title: e.school || 'Nouvelle formation', sub: [[e.degree, e.field].filter(Boolean).join(', '), formatRange(e.start, e.end, false, false)].filter(Boolean).join(' · ') }),
    fields: [
      { name: 'school', label: 'Établissement', required: true, placeholder: 'ex : Université Paris-Saclay' },
      { tr: true, name: 'degree', label: 'Diplôme', placeholder: 'ex : Master' },
      { tr: true, name: 'field', label: 'Domaine d\'études', placeholder: 'ex : Informatique' },
      { tr: true, name: 'grade', label: 'Mention / résultat', placeholder: 'ex : Mention Bien' },
      { name: 'start', label: 'Début', type: 'month' },
      { name: 'end', label: 'Fin (ou prévue)', type: 'month' },
      { name: 'logo', label: 'Logo (facultatif)', type: 'image', full: true },
      { tr: true, name: 'description', label: 'Description', type: 'textarea', full: true, placeholder: 'Activités, projets, spécialités…' },
    ],
  },
  certifications: {
    label: 'Certifications', item: 'certification',
    empty: () => ({ id: newId(), name: '', issuer: '', issue_date: '', expiry_date: '', credential_id: '', url: '', logo: '' }),
    summary: (c) => ({ title: c.name || 'Nouvelle certification', sub: [c.issuer, c.issue_date && `délivrée en ${formatMonth(c.issue_date)}`].filter(Boolean).join(' · ') }),
    fields: [
      { tr: true, name: 'name', label: 'Nom de la certification', required: true, placeholder: 'ex : AWS Certified Cloud Practitioner' },
      { name: 'issuer', label: 'Organisme', required: true, placeholder: 'ex : Amazon Web Services' },
      { name: 'issue_date', label: 'Date d\'obtention', type: 'month' },
      { name: 'expiry_date', label: 'Date d\'expiration (facultative)', type: 'month' },
      { name: 'credential_id', label: 'ID de la certification' },
      { name: 'url', label: 'Lien de vérification', type: 'url', placeholder: 'https://…' },
      { name: 'logo', label: 'Logo (facultatif)', type: 'image', full: true },
    ],
  },
};

const PROFILE_FIELDS = [
  { name: 'name', label: 'Nom complet', placeholder: 'Fatima Zahra Moussaid' },
  { tr: true, name: 'headline', label: 'Titre du profil', placeholder: 'ex : Ingénieure cloud · AWS · Node.js' },
  { tr: true, name: 'location', label: 'Localisation', placeholder: 'ex : Paris, Île-de-France, France' },
  { name: 'photo', label: 'Photo de profil', type: 'image' },
  { tr: true, name: 'about', label: 'Présentation générale (« Infos »)', type: 'textarea', full: true, rows: 8, placeholder: 'Qui vous êtes, ce que vous faites, ce que vous cherchez… (retours à la ligne conservés)' },
  { name: 'email', label: 'E-mail de contact', type: 'email' },
  { name: 'phone', label: 'Téléphone (facultatif)' },
  { name: 'linkedin', label: 'Profil LinkedIn', type: 'url', placeholder: 'https://www.linkedin.com/in/…' },
  { name: 'website', label: 'Site web', type: 'url', placeholder: 'https://…' },
];

// Mêmes règles que le serveur
function validate(cv) {
  const errs = [];
  const okUrl = (v) => !v || /^https?:\/\//i.test(v) || v.startsWith('/uploads/');
  ['linkedin', 'website'].forEach(k => { if (!okUrl(cv.profile[k])) errs.push(`Profil : le lien « ${k} » doit commencer par https://`); });
  cv.experiences.forEach((e, i) => {
    if (!e.title.trim() || !e.company.trim()) errs.push(`Expérience ${i + 1} : le poste et l'entreprise sont obligatoires`);
    if (!e.start) errs.push(`Expérience ${i + 1} : la date de début est obligatoire`);
    if (e.start && e.end && !e.current && e.end < e.start) errs.push(`Expérience ${i + 1} : la date de fin est avant la date de début`);
  });
  cv.education.forEach((e, i) => {
    if (!e.school.trim()) errs.push(`Formation ${i + 1} : l'établissement est obligatoire`);
    if (e.start && e.end && e.end < e.start) errs.push(`Formation ${i + 1} : la date de fin est avant la date de début`);
  });
  cv.certifications.forEach((c, i) => {
    if (!c.name.trim() || !c.issuer.trim()) errs.push(`Certification ${i + 1} : le nom et l'organisme sont obligatoires`);
    if (!okUrl(c.url)) errs.push(`Certification ${i + 1} : le lien doit commencer par https://`);
  });
  return errs;
}

// Champs traduisibles : en mode « English », on édite la clé « _en » et le texte français sert d'aide
const keyFor = (f, lang) => (lang === 'en' && f.tr ? `${f.name}_en` : f.name);
const countMissing = (cv) => {
  let n = 0;
  const scan = (obj, fields) => fields.forEach(f => { if (f.tr && obj[f.name] && !(obj[`${f.name}_en`] || '').trim()) n++; });
  scan(cv.profile, PROFILE_FIELDS);
  Object.entries(SECTIONS).forEach(([k, sec]) => cv[k].forEach(it => scan(it, sec.fields)));
  return n;
};

function Field({ f, value, item, onChange, uploadImage, onError, lang = 'fr', frValue }) {
  const en = lang === 'en';
  const placeholder = en && f.tr ? (frValue ? `FR : ${frValue.replace(/\s+/g, ' ').slice(0, 90)}` : 'English version') : f.placeholder;
  const labelText = en && f.tr ? <>{f.label} <span className="cv-lang-tag">EN</span></> : en ? <>{f.label} <span className="cv-common">commun FR / EN</span></> : <>{f.label}{f.required && ' *'}</>;
  const [uploading, setUploading] = useState(false);
  const common = { id: `f-${f.name}-${lang}-${item && item.id}` };
  let input;
  if (f.type === 'textarea') {
    input = <textarea {...common} className="cv-textarea" rows={f.rows || 5} value={value || ''} placeholder={placeholder} onChange={e => onChange(e.target.value)} />;
  } else if (f.type === 'select') {
    input = (
      <select {...common} value={value || ''} onChange={e => onChange(e.target.value)}>
        {f.options.map(o => <option key={o} value={o}>{o || '—'}</option>)}
      </select>
    );
  } else if (f.type === 'checkbox') {
    return (
      <div className="form-group cv-full">
        <label className="cv-check"><input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} /> {f.label}</label>
      </div>
    );
  } else if (f.type === 'image') {
    const onFile = async (e) => {
      const file = e.target.files[0]; if (!file) return;
      setUploading(true);
      try { onChange(await uploadImage(file)); }
      catch (err) { onError(`Image non ajoutée : ${err.message}`); }
      finally { setUploading(false); e.target.value = ''; }
    };
    input = (
      <div className="cv-image-field">
        {value ? <img src={value} alt="" /> : <span className="cv-image-empty">aucune</span>}
        <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={onFile} disabled={uploading} />
        {uploading && <span>envoi…</span>}
        {value && <button type="button" className="qcm-link-btn" onClick={() => onChange('')}>retirer</button>}
      </div>
    );
  } else {
    const disabled = f.disabledIf && item && item[f.disabledIf];
    input = <input {...common} type={f.type || 'text'} value={disabled ? '' : (value || '')} placeholder={placeholder} disabled={disabled}
      onChange={e => onChange(e.target.value)} />;
  }
  return (
    <div className={`form-group ${f.full ? 'cv-full' : ''}`}>
      <label htmlFor={common.id}>{labelText}</label>
      {input}
    </div>
  );
}

export default function AdminCv() {
  const { authFetch, uploadImage } = useAuth();
  const [cv, setCv] = useState(null);
  const [tab, setTab] = useState('profile');
  const [lang, setLang] = useState('fr'); // langue du contenu édité
  const [openId, setOpenId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [flash, setFlash] = useState(null);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [base, setBase] = useState(null);       // version du CV au moment du chargement (anti-écrasement)
  const [history, setHistory] = useState(null);  // versions précédentes (null = masquées)

  useEffect(() => {
    fetch('/api/cv', { cache: 'no-store' })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => { setBase(d.updated_at || null); setCv({ profile: d.profile || {}, experiences: d.experiences || [], education: d.education || [], certifications: d.certifications || [] }); })
      .catch(() => setFlash({ type: 'error', text: 'Chargement du CV impossible' }));
  }, []);

  useEffect(() => {
    if (!dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const update = (fn) => { setCv(prev => fn(prev)); setDirty(true); };
  const setProfile = (name, v) => update(c => ({ ...c, profile: { ...c.profile, [name]: v } }));
  const setItem = (sec, itemId, name, v) => update(c => ({
    ...c, [sec]: c[sec].map(it => (it.id !== itemId ? it : { ...it, [name]: v, ...(name === 'current' && v ? { end: '' } : {}) }))
  }));
  const addItem = (sec) => {
    const it = SECTIONS[sec].empty();
    update(c => ({ ...c, [sec]: [it, ...c[sec]] }));
    setOpenId(it.id);
  };
  const removeItem = (sec, it) => {
    const label = SECTIONS[sec].summary(it).title;
    if (!window.confirm(`Supprimer « ${label} » ?`)) return;
    update(c => ({ ...c, [sec]: c[sec].filter(x => x.id !== it.id) }));
  };
  const moveItem = (sec, idx, dir) => update(c => {
    const arr = [...c[sec]]; const t = idx + dir;
    if (t < 0 || t >= arr.length) return c;
    [arr[idx], arr[t]] = [arr[t], arr[idx]];
    return { ...c, [sec]: arr };
  });
  const sortItems = (sec) => update(c => ({
    ...c, [sec]: [...c[sec]].sort(sec === 'certifications' ? byRecent('issue_date', 'issue_date') : byRecent('start'))
  }));

  const save = async () => {
    const errs = validate(cv);
    setErrors(errs);
    if (errs.length) { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setSaving(true); setFlash(null);
    try {
      const r = await authFetch('/api/admin/cv', { method: 'PUT', body: JSON.stringify({ ...cv, base_updated_at: base }) });
      if (!r.ok) throw new Error(await errorMessage(r));
      const saved = await r.json().catch(() => null);
      if (saved && saved.cv) setBase(saved.cv.updated_at || null);
      setDirty(false);
      setFlash({ type: 'ok', text: '✓ CV enregistré' });
      setTimeout(() => setFlash(f => (f && f.text === '✓ CV enregistré' ? null : f)), 2500);
    } catch (err) {
      setFlash({ type: 'error', text: `Non enregistré : ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = async () => {
    try {
      const r = await authFetch('/api/admin/cv/history');
      if (!r.ok) throw new Error(await errorMessage(r));
      setHistory(await r.json());
    } catch (err) { setFlash({ type: 'error', text: `Historique indisponible : ${err.message}` }); }
  };
  const restore = async (v) => {
    if (!window.confirm(`Restaurer la version du ${v.saved_at} ? (${v.experiences} expériences, ${v.certifications} certifications). La version actuelle sera elle aussi conservée dans l'historique.`)) return;
    try {
      const r = await authFetch(`/api/admin/cv/restore/${v.id}`, { method: 'POST' });
      if (!r.ok) throw new Error(await errorMessage(r));
      const d = (await r.json()).cv;
      setBase(d.updated_at || null);
      setCv({ profile: d.profile || {}, experiences: d.experiences || [], education: d.education || [], certifications: d.certifications || [] });
      setDirty(false); setHistory(null); setErrors([]);
      setFlash({ type: 'ok', text: '✓ Version restaurée' });
    } catch (err) { setFlash({ type: 'error', text: `Restauration impossible : ${err.message}` }); }
  };

  if (!cv) return <div>{flash ? <div className="flash error">{flash.text}</div> : 'chargement…'}</div>;

  const onError = (text) => setFlash({ type: 'error', text });
  const TABS = [['profile', 'Présentation'], ...Object.entries(SECTIONS).map(([k, v]) => [k, `${v.label} (${cv[k].length})`])];

  return (
    <div>
      <div className="cv-admin-head">
        <h1 style={{ fontStyle: 'italic' }}>CV</h1>
        <a href="/cv" target="_blank" rel="noreferrer" className="btn secondary">voir le CV ↗</a>
      </div>

      {flash && <div className={`flash ${flash.type === 'error' ? 'error' : ''}`}>{flash.text}</div>}
      {errors.length > 0 && (
        <div className="flash error"><strong>À corriger avant d'enregistrer :</strong><ul>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul></div>
      )}

      <div className="cv-lang-bar">
        <span>Langue du contenu</span>
        <div className="cv-lang-switch" role="group" aria-label="Langue du contenu">
          <button type="button" aria-pressed={lang === 'fr'} className={lang === 'fr' ? 'on' : ''} onClick={() => setLang('fr')}>Français</button>
          <button type="button" aria-pressed={lang === 'en'} className={lang === 'en' ? 'on' : ''} onClick={() => setLang('en')}>English</button>
        </div>
        {lang === 'en' && (() => { const m = countMissing(cv); return (
          <span className={`cv-lang-status ${m ? '' : 'ok'}`}>{m ? `${m} texte(s) pas encore traduit(s) — le français sera affiché à la place` : '✓ tout est traduit'}</span>
        ); })()}
      </div>

      <div className="cv-tabs" role="tablist">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => { setTab(k); setOpenId(null); }}>{label}</button>
        ))}
      </div>

      {tab === 'profile' ? (
        <div className="qcm-panel cv-form-grid">
          {PROFILE_FIELDS.map(f => (
            <Field key={keyFor(f, lang)} f={f} lang={lang} frValue={cv.profile[f.name]} value={cv.profile[keyFor(f, lang)]} onChange={v => setProfile(keyFor(f, lang), v)} uploadImage={uploadImage} onError={onError} />
          ))}
        </div>
      ) : (
        <div>
          <div className="qcm-actions-bar">
            <button type="button" className="btn" onClick={() => addItem(tab)}>+ ajouter une {SECTIONS[tab].item}</button>
            {cv[tab].length > 1 && <button type="button" className="btn secondary" onClick={() => sortItems(tab)}>trier du plus récent au plus ancien</button>}
          </div>
          {cv[tab].length === 0 && <div className="empty-state">aucune {SECTIONS[tab].item} pour l'instant.</div>}
          {cv[tab].map((it, idx) => {
            const sum = SECTIONS[tab].summary(it);
            const open = openId === it.id;
            return (
              <div key={it.id} className={`cv-item-editor ${open ? 'open' : ''}`}>
                <div className="cv-item-head">
                  <button type="button" className="cv-item-toggle" onClick={() => setOpenId(open ? null : it.id)} aria-expanded={open}>
                    <span className="cv-item-caret">{open ? '▾' : '▸'}</span>
                    <span>
                      <strong>{sum.title}</strong>
                      {sum.sub && <small>{sum.sub}</small>}
                    </span>
                  </button>
                  <div className="admin-row-actions">
                    <button type="button" className="btn secondary" onClick={() => moveItem(tab, idx, -1)} disabled={idx === 0} title="monter">↑</button>
                    <button type="button" className="btn secondary" onClick={() => moveItem(tab, idx, 1)} disabled={idx === cv[tab].length - 1} title="descendre">↓</button>
                    <button type="button" className="btn danger" onClick={() => removeItem(tab, it)} title="supprimer">✕</button>
                  </div>
                </div>
                {open && (
                  <div className="cv-form-grid">
                    {SECTIONS[tab].fields.map(f => (
                      <Field key={keyFor(f, lang)} f={f} lang={lang} frValue={it[f.name]} item={it} value={it[keyFor(f, lang)]} onChange={v => setItem(tab, it.id, keyFor(f, lang), v)} uploadImage={uploadImage} onError={onError} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="qcm-panel" style={{ marginTop: '2rem' }}>
        <button type="button" className="qcm-link-btn" onClick={() => (history ? setHistory(null) : loadHistory())}>
          {history ? '▾ masquer les versions précédentes' : '▸ versions précédentes (restaurer en cas d\'erreur)'}
        </button>
        {history && (history.length === 0
          ? <p style={{ marginTop: '.6rem' }}>Aucune version enregistrée pour l'instant : une copie est faite avant chaque enregistrement.</p>
          : <ul className="cv-history">
              {history.map(v => (
                <li key={v.id}>
                  <span>{v.saved_at} — {v.experiences} exp. · {v.education} form. · {v.certifications} certif.</span>
                  <button type="button" className="btn secondary" onClick={() => restore(v)}>restaurer</button>
                </li>
              ))}
            </ul>)}
      </div>

      <div className="qcm-savebar" style={{ marginTop: '1.5rem' }}>
        <span>{dirty ? '● modifications non enregistrées' : '✓ à jour'}</span>
        <button type="button" className="btn" onClick={save} disabled={saving}>{saving ? 'enregistrement…' : 'enregistrer le CV'}</button>
      </div>
    </div>
  );
}
